import { jsx } from "react/jsx-runtime";
//#region src/env/util.environment.tsx
var publicPostfix = "__PUBLIC";
var envRef = { current: deserializePublicEnvironment() };
var envOverrideRef = { current: {} };
var listeners = /* @__PURE__ */ new Set();
var environment = Object.freeze({
	/**
	* reads an environment variable
	*
	* NOTE that this method will throw early if the variable hasn't been defined,
	* as we wish to enforce explicit configurations rather than having implicit
	* defaults in some cases
	*/
	read(key, options) {
		const envVars = getEnvironmentVariables();
		const envPostfix = (() => {
			var _envVars$APP_ENV;
			const appEnv = (_envVars$APP_ENV = envVars["APP_ENV"]) === null || _envVars$APP_ENV === void 0 ? void 0 : _envVars$APP_ENV.toUpperCase();
			switch (appEnv) {
				case "TEST":
				case "STAGING":
				case "PRODUCTION": return `__${appEnv}`;
				default: return "__TEST";
			}
		})();
		const value = envVars[`${key}${envPostfix}`] ?? envVars[`${key}${envPostfix}${publicPostfix}`] ?? envVars[key] ?? envVars[`${key}${publicPostfix}`];
		if (value === void 0 && !(options === null || options === void 0 ? void 0 : options.optional)) throw new ReferenceError(`environment.read(): referencing undefined key "${key}".`);
		return value;
	},
	/**
	* override the in-memory record of available environment variables
	*/
	override(nextEnv) {
		envOverrideRef.current = {
			...envOverrideRef.current,
			...nextEnv
		};
		listeners.forEach((listener) => {
			listener();
		});
	},
	/**
	* enable observing of changes within the available environment variables, so
	* that other sources can listen for changes and react to them accordingly
	*/
	observe(listener) {
		const uniqueListener = () => {
			listener();
		};
		listeners.add(uniqueListener);
		return () => listeners.delete(uniqueListener);
	}
});
/**
* returns the currently applied environment variables, falling back to
* process.env if nothing has been explicitly applied as an override
*
* this setup allows us to propagate environment variables from the server side
* to the client side layer in next.js applications, in a way where we are not
* restricted to applying build-time configurations only
*
* this is an essential feature, in order to allow re-using the same docker
* images across multiple environments, as we need a way to configure e.g.
* api references differently across each of these
*/
function getEnvironmentVariables() {
	return {
		...envRef.current ?? process.env,
		...envOverrideRef.current
	};
}
/**
* serializes all environment variables that starts with the specified public
* prefix to a JSX script tag, which should be flushed during server side
* rendering of a page to synchronize environment variables between the server
* and the client
*
* these values can then be included in the HTML output, and therefore allows
* deserializing dynamic configurations in a browser context
*
* @example flushing environment variables in Next.js
* ```tsx
* import Document, { Head, Html, Main, NextScript } from 'next/document';
*
* const publicEnv = configFromEnv.serializePublicEnvironment();
*
* export default class CustomDocument extends Document {
*   render() {
*     return (
*       <Html lang="da">
*         <Head />
*         <body className={resets.rootCls}>
*           <Main />
*           {publicEnv.current}
*           <NextScript />
*         </body>
*       </Html>
*     );
*   }
* }
* ```
*/
var ALWAYS_GLOBAL_KEYS = ["NODE_ENV", "APP_ENV"];
function serializePublicEnvironment() {
	function serialize() {
		const serializedOutput = JSON.stringify(Object.fromEntries(Object.entries(getEnvironmentVariables()).filter(([key]) => key.endsWith(publicPostfix) || ALWAYS_GLOBAL_KEYS.includes(key)).map(([key, value]) => [key.replace(/__PUBLIC$/, ""), value])));
		return /* @__PURE__ */ jsx("script", {
			type: "text/json",
			"data-gyl--env": true,
			dangerouslySetInnerHTML: { __html: serializedOutput }
		});
	}
	const publicEnv = { current: serialize() };
	environment.observe(() => {
		publicEnv.current = serialize();
	});
	return publicEnv;
}
/**
* deserialize and apply public environment variables within a browser-based
* environment
*/
function deserializePublicEnvironment() {
	var _document$querySelect;
	if (typeof document === "undefined") return;
	const serializedEnvVars = (_document$querySelect = document.querySelector("script[data-gyl--env]")) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.textContent;
	return JSON.parse(serializedEnvVars ?? "null");
}
//#endregion
export { environment, serializePublicEnvironment };

//# sourceMappingURL=util.environment.js.map